import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Zap } from "lucide-react";

import {
  getKBEquipmentType,
  type KBConfigOptionGroup,
} from "@/lib/api/agent";
import {
  criterionSourceLabels,
  ruleSourcesByCriterionId,
} from "@/lib/kb-provenance";
import { KBBehaviorList } from "@/components/configuration/KBBehaviorList";
import {
  listEquipmentConfigParameters,
  listEquipmentContextSetpoints,
  listEquipmentFindings,
  setEquipmentConfigParameter,
  type ConfigParameterValue,
  type ContextSetpoint,
  type DataPointSource,
  type Finding,
} from "@/lib/api/building";
import {
  resolvedEquipmentZoneIds,
  type EquipmentCard,
  type Zone,
} from "@/lib/building-domain";
import {
  equipmentTypeIcon,
  equipmentTypeNameFi,
  useEquipmentTypeMeta,
  useKbSensorTypes,
} from "@/lib/equipment-type-meta";
import { cardSubtle, inputGlowInner, inputGlowWrap } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

import { FindingsList } from "../findings/FindingsList";
import { OPEN_AND_ACKNOWLEDGED } from "../findings/findings-helpers";
import { DataPointMappingPanel } from "./DataPointMappingPanel";
import { DataPointsExplorer } from "../datapoints/DataPointsExplorer";
import { build_equipment_visible_signals } from "./equipment-signal-filter";

// ── EquipmentDetailPanel ──────────────────────────────────────────────────────

interface DetailPanelProps {
  buildingId: string;
  equipment: EquipmentCard;
  zones: Zone[];
  /** @deprecated DataPointsExplorer derives behavior labels from the KB internally. */
  behavior_labels?: Record<string, string>;
  operator: boolean;
  on_update: (
    id: string,
    data: { optimization_enabled?: boolean },
  ) => Promise<void>;
  on_close: () => void;
}

export function EquipmentDetailPanel({
  buildingId,
  equipment,
  zones,
  operator,
  on_update,
  on_close,
}: DetailPanelProps) {
  const eqMeta = useEquipmentTypeMeta();
  const sensors = useKbSensorTypes();
  const [optimization_saving, set_optimization_saving] = useState(false);

  async function toggle_optimization_enabled() {
    set_optimization_saving(true);
    try {
      await on_update(equipment.id, {
        optimization_enabled: !equipment.optimization_enabled,
      });
      toast.success(
        equipment.optimization_enabled
          ? "Optimointi pois päältä tälle laitteelle"
          : "Optimointi käytössä tälle laitteelle",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Päivitys epäonnistui");
    } finally {
      set_optimization_saving(false);
    }
  }
  const [findings, set_findings] = useState<Finding[]>([]);
  const [findings_loading, set_findings_loading] = useState(true);

  useEffect(() => {
    set_findings_loading(true);
    listEquipmentFindings(buildingId, equipment.id)
      .then(set_findings)
      .catch(() => {})
      .finally(() => set_findings_loading(false));
  }, [buildingId, equipment.id]);

  // Empty for equipment whose KB type declares no context-setpoint family — safe to fetch
  // unconditionally for every equipment type (docs/spec/knowledge-base.md § Context
  // setpoints — suggestion and accept).
  const [context_setpoints, set_context_setpoints] = useState<
    ContextSetpoint[]
  >([]);

  useEffect(() => {
    listEquipmentContextSetpoints(buildingId, equipment.id)
      .then(set_context_setpoints)
      .catch(() => set_context_setpoints([]));
  }, [buildingId, equipment.id]);

  const [option_groups, set_option_groups] = useState<KBConfigOptionGroup[]>(
    [],
  );
  const [declared_variants, set_declared_variants] = useState<string[]>(
    equipment.declared_variants,
  );
  const [declared_data_points, set_declared_data_points] = useState<string[]>(
    equipment.declared_data_points ?? [],
  );

  const [config_parameters, set_config_parameters] = useState<
    ConfigParameterValue[]
  >([]);
  const [config_reload, set_config_reload] = useState(0);
  const [rule_source_labels, set_rule_source_labels] = useState<
    Map<string, string[]>
  >(new Map());

  useEffect(() => {
    listEquipmentConfigParameters(buildingId, equipment.id)
      .then(set_config_parameters)
      .catch(() => set_config_parameters([]));
  }, [buildingId, equipment.id, config_reload]);

  useEffect(() => {
    getKBEquipmentType(equipment.equipment_type)
      .then((def) => {
        set_option_groups(def.config_option_groups ?? []);
        // A finding's `finding_type` is the criterion that raised it, so the type's criteria
        // name the standard behind each finding's rule (REQ-AGT-28).
        set_rule_source_labels(
          new Map(
            [...ruleSourcesByCriterionId(def)].map(([id, citations]) => [
              id,
              criterionSourceLabels(citations),
            ]),
          ),
        );
      })
      .catch(() => {
        set_option_groups([]);
        set_rule_source_labels(new Map());
      });
  }, [equipment.equipment_type]);

  useEffect(() => {
    set_declared_variants(equipment.declared_variants);
  }, [equipment.declared_variants]);

  const served_zones = resolvedEquipmentZoneIds(equipment)
    .map((id) => zones.find((z) => z.id === id))
    .filter((z): z is Zone => z !== undefined);

  const [mapping_open, set_mapping_open] = useState(false);

  const resolve_equipment_visible_signals = useCallback(
    (sources: DataPointSource[]) => {
      const src = sources.find(
        (s) => s.source_id === equipment.id && s.source_type === "equipment",
      );
      if (!src) return [];
      // This-item scope only — not "mapped only". Activated names (Käytössä + asetusarvo
      // families) stay visible even when they are not in available_names (#1515).
      return build_equipment_visible_signals(
        src.source_id,
        src.available_names.map((n) => n.name),
        [
          ...declared_data_points,
          ...context_setpoints.map((cs) => cs.context_setpoint_id),
        ],
      );
    },
    [equipment.id, declared_data_points, context_setpoints],
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={cn(
        cardSubtle,
        "col-span-full mt-1 border-primary/20 bg-card/80 p-5",
      )}
    >
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl">
              {equipmentTypeIcon(eqMeta, equipment.equipment_type, sensors)}
            </span>
            <h3 className="text-sm font-semibold">{equipment.name}</h3>
            {equipment.equipment_tag && (
              <span className="font-mono text-xs text-muted-foreground">
                {equipment.equipment_tag}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {equipmentTypeNameFi(eqMeta, equipment.equipment_type, sensors)}
            {equipment.location && ` · ${equipment.location}`}
            {served_zones.length > 0 &&
              ` · Palvelee: ${served_zones.map((z) => z.name).join(", ")}`}
          </p>
        </div>
        <button
          onClick={on_close}
          className="shrink-0 rounded px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
        >
          Sulje ✕
        </button>
      </div>

      {operator && (
        <div
          className={cn(
            cardSubtle,
            "mb-4 flex items-center justify-between gap-4 p-3",
          )}
        >
          <p className="text-xs text-muted-foreground max-w-prose">
            Kun käytössä, optimointisilmukka voi luoda reseptejä tälle
            laitteelle. Rakennuksen Automaattinen optimointi -kytkin päättää
            erikseen, sovelletaanko ne automaattisesti.
          </p>
          <button
            type="button"
            onClick={toggle_optimization_enabled}
            disabled={optimization_saving}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50",
              equipment.optimization_enabled
                ? "bg-success-subtle text-success-subtle-foreground hover:bg-success/20"
                : "bg-muted text-muted-foreground hover:bg-muted/70",
            )}
          >
            {optimization_saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Zap className="h-3.5 w-3.5" />
            )}
            {equipment.optimization_enabled ? "Käytössä" : "Pois päältä"}
          </button>
        </div>
      )}

      {operator && (
        <FanKUnlockCard
          buildingId={buildingId}
          equipmentId={equipment.id}
          declaredDataPoints={declared_data_points}
          configParameters={config_parameters}
          onSaved={() => set_config_reload((n) => n + 1)}
        />
      )}

      {/* ── Context setpoints: read-only pointer to their one editable home (story #1515) ──
          Konfiguraatio → Signaalit owns the accept-suggestion / typed-fill actions (#1528/#1529)
          and Datan laatu → Signaalit owns the live effective-value read-out; this panel
          only names that the equipment has asetusarvo families, it performs no write. */}
      {context_setpoints.length > 0 && (
        <section className="mb-5">
          <p className={cn(cardSubtle, "p-3 text-xs text-muted-foreground")}>
            {context_setpoints.length === 1
              ? "Tällä laitteella on asetusarvo, "
              : `Tällä laitteella on ${context_setpoints.length} asetusarvoa, `}
            jonka hyväksyntä ja syöttö tehdään Konfiguraatio → Signaalit.
          </p>
        </section>
      )}

      {/* ── Monitoring mode: live data → findings ── */}
      {!operator && (
        <>
          <section className="mb-5">
            <DataPointsExplorer
              buildingId={buildingId}
              resolveVisibleSignals={resolve_equipment_visible_signals}
            />
          </section>

          <FindingsList
            findings={findings}
            loading={findings_loading}
            ruleSourceLabels={rule_source_labels}
            sourceId={equipment.id}
            defaultStatuses={OPEN_AND_ACKNOWLEDGED}
            showStatusFilter={false}
            emptyMessage="Ei avoimia havaintoja."
            onAcknowledged={(updated) =>
              set_findings((prev) =>
                prev.map((f) => (f.id === updated.id ? updated : f)),
              )
            }
          />
        </>
      )}

      {/* ── Config mode: behaviors → data points → findings ── */}
      {operator && (
        <>
          {/* Variant selection is READ-ONLY here. Its one editable home is the
              "Kyvykkyydet & odotettu toiminta" tab, where its effect — which capabilities
              the equipment has — is visible next to it. See docs/spec/concepts.md
              §2.6 (tab roles) and §2.7 (one editable home per persisted field). */}
          {option_groups.length > 0 && (
            <section className="mb-5 flex flex-col gap-3">
              {option_groups.map((group) => {
                const chosen =
                  group.type === "boolean"
                    ? declared_variants.includes(group.id)
                      ? group.name_fi
                      : null
                    : (group.choices.find((c) =>
                        declared_variants.includes(c.id),
                      )?.name_fi ?? null);
                return (
                  <div key={group.id}>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {group.name_fi}
                    </p>
                    <p className="text-xs">
                      {chosen ?? (
                        <span className="text-muted-foreground">
                          Ei valittu
                        </span>
                      )}
                    </p>
                  </div>
                );
              })}
              <p className="text-[10.5px] text-muted-foreground/70">
                Kokoonpanoa muokataan Kyvykkyydet-välilehdellä.
              </p>
            </section>
          )}

          <section className="mb-5">
            <KBBehaviorList
              buildingId={buildingId}
              equipmentId={equipment.id}
            />
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Signaalit
              </p>
              {!mapping_open && operator && (
                <button
                  onClick={() => set_mapping_open(true)}
                  className="rounded bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/20"
                >
                  Hallitse kytkentöjä →
                </button>
              )}
            </div>
            {mapping_open ? (
              <DataPointMappingPanel
                buildingId={buildingId}
                equipmentId={equipment.id}
                operator={operator}
                initial_declared={declared_data_points}
                on_declared_changed={set_declared_data_points}
                on_close={() => set_mapping_open(false)}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                Tuo laitteen CSV-data — sarakkeet yhdistetään automaattisesti
                KB-signaaleihin.
              </p>
            )}
          </section>

          <FindingsList
            findings={findings}
            loading={findings_loading}
            ruleSourceLabels={rule_source_labels}
            sourceId={equipment.id}
            defaultStatuses={OPEN_AND_ACKNOWLEDGED}
            showStatusFilter={false}
            emptyMessage="Ei avoimia havaintoja."
            onAcknowledged={(updated) =>
              set_findings((prev) =>
                prev.map((f) => (f.id === updated.id ? updated : f)),
              )
            }
          />
        </>
      )}
    </div>
  );
}

const DUCT_PRESSURE_IDS = new Set([
  "supply_duct_pressure",
  "extract_duct_pressure",
]);

/** When duct pressure is wired but fan_k is unset, recommend entering k to unlock derived airflow. */
function FanKUnlockCard({
  buildingId,
  equipmentId,
  declaredDataPoints,
  configParameters,
  onSaved,
}: {
  buildingId: string;
  equipmentId: string;
  declaredDataPoints: string[];
  configParameters: ConfigParameterValue[];
  onSaved: () => void;
}) {
  const fanK = configParameters.find((p) => p.parameter_id === "fan_k");
  const ductPressureWired = declaredDataPoints.some((id) =>
    DUCT_PRESSURE_IDS.has(id),
  );
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!fanK || fanK.value !== null || !ductPressureWired) return null;

  async function save() {
    const num = Number(value.replace(",", "."));
    if (value.trim() === "" || Number.isNaN(num)) {
      setError("Anna numeroarvo");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await setEquipmentConfigParameter(buildingId, equipmentId, "fan_k", num);
      toast.success(
        "fan_k tallennettu — lasketut signaalit avautuvat seuraavalla KB-kierroksella",
      );
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={cn(cardSubtle, "mb-5 p-3 space-y-2")}>
      <p className="text-xs font-semibold text-warning-subtle-foreground">
        Suositus: anna puhaltimen k-arvo
      </p>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Kanavapaine on kytketty. Kun {fanK.name_fi || "fan_k"} on asetettu,
        Kairos laskee ilmavirran (k · Δp) ja siitä LTO:n hetkelliset tehot.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <div className={cn(inputGlowWrap, "w-40")}>
          <div className={inputGlowInner}>
            <input
              type="text"
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={fanK.unit ? `arvo (${fanK.unit})` : "arvo"}
              className="w-full bg-transparent px-2 py-1 text-xs placeholder:text-muted-foreground outline-none"
            />
          </div>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="rounded px-2.5 py-1 text-xs bg-primary/10 text-primary hover:bg-primary/15 disabled:opacity-50"
        >
          {saving ? "…" : "Tallenna"}
        </button>
      </div>
      {error && (
        <p className="text-[11px] text-destructive-subtle-foreground">
          {error}
        </p>
      )}
    </section>
  );
}
